import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "../../../i18n/LanguageContext";
import {
  faBell,
  faCheck,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../common/LoadingSpinner";
import NotificationItem from "./NotificationItem";
import NotificationBadge from "./NotificationBadge";
import { useNotification } from "../context/NotificationContext";
import { useUser } from "../context/UserContext";
import EmailAnalysisAPI from "../services/EmailAnalysisAPI";
import "./css/NotificationFullPage.css";
import "./css/NotificationDropdown.css";

/**
 * Notification dropdown component
 */
function NotificationDropdown({
  setHeroContent,
  className = "",
  disabled = false,
  disabledTitle,
  isActive = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [
    verificationFailedNotificationId,
    setVerificationFailedNotificationId,
  ] = useState(null);
  const lastLoadTimeRef = useRef(0);
  const dropdownRef = useRef(null);
  const { t } = useTranslation();
  const { loggedInUser } = useUser();
  const {
    unreadCount,
    notifications,
    loading,
    error,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotifications,
    setError,
  } = useNotification();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Listen for cancel verification event
  useEffect(() => {
    const handleCancelVerification = () => {
      console.log("NotificationDropdown: Cancel verification requested");
      setVerificationFailedNotificationId(null);
      setIsOpen(false);
    };

    window.addEventListener(
      "cancel-notification-verification",
      handleCancelVerification,
    );

    return () => {
      window.removeEventListener(
        "cancel-notification-verification",
        handleCancelVerification,
      );
    };
  }, []);

  // Listen for analysis-not-found event (fired when Email Analysis page cannot load an analysis)
  useEffect(() => {
    const handleAnalysisNotFound = (event) => {
      const { analysisKey } = event.detail || {};
      if (!analysisKey) return;
      const notificationsList = Array.isArray(notifications)
        ? notifications
        : [];
      const targetFilename = analysisKey.split("/").pop();
      const matched = notificationsList.find((n) => {
        try {
          const action =
            typeof n.actionData === "string"
              ? JSON.parse(n.actionData)
              : n.actionData;
          const key = action?.params?.analysisKey;
          return (
            key &&
            (key === analysisKey ||
              key.endsWith(targetFilename) ||
              analysisKey.endsWith(key.split("/").pop()))
          );
        } catch {
          return false;
        }
      });
      if (matched) {
        console.log(
          "NotificationDropdown: Analysis not found, showing delete option for notification:",
          matched.notificationId,
        );
        setVerificationFailedNotificationId(matched.notificationId);
        setIsOpen(true);
      }
    };
    window.addEventListener("email-analysis-not-found", handleAnalysisNotFound);
    return () => {
      window.removeEventListener(
        "email-analysis-not-found",
        handleAnalysisNotFound,
      );
    };
  }, [notifications]);

  // Load notifications when opened, but not if we're already on the Notifications page
  useEffect(() => {
    if (isOpen && loggedInUser?.uuid) {
      const now = Date.now();
      // Only load if it's been more than 2 seconds since last load
      if (now - lastLoadTimeRef.current > 2000) {
        lastLoadTimeRef.current = now;
        loadNotifications();
      }
    }
  }, [isOpen, loggedInUser]);

  // Load notification counts on component mount and periodically
  useEffect(() => {
    if (loggedInUser?.uuid) {
      // Polling is now handled by NotificationContext
      // This component just needs to load notifications when opened
    }
  }, [loggedInUser]);

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId) => {
    if (!loggedInUser?.uuid) return;

    try {
      await markAsRead([notificationId]);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      throw error;
    }
  };

  const handleDismiss = async (notificationId) => {
    if (!loggedInUser?.uuid) return;

    try {
      await dismissNotifications([notificationId]);
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
      throw error;
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!loggedInUser?.uuid) return;

    try {
      await markAllAsRead();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleNotificationAction = async (actionData, notificationId) => {
    try {
      const parsedAction =
        typeof actionData === "string" ? JSON.parse(actionData) : actionData;

      console.log(
        "NotificationDropdown: Handling notification action:",
        parsedAction,
      );

      if (parsedAction.type === "navigate" && parsedAction.route) {
        console.log(
          "NotificationDropdown: Navigation action detected, route:",
          parsedAction.route,
        );

        // For email analysis notifications, verify the analysis exists before navigating
        if (
          parsedAction.route === "EmailAnalysis" &&
          parsedAction.params?.analysisKey
        ) {
          const analysisKey = parsedAction.params.analysisKey;
          console.log(
            "NotificationDropdown: Verifying analysis exists for key:",
            analysisKey,
          );

          // Pre-flight check: does the analysis exist?
          const exists =
            await EmailAnalysisAPI.checkAnalysisExists(analysisKey);

          console.log(
            "NotificationDropdown: Verification result - exists:",
            exists,
          );

          if (!exists) {
            // Analysis doesn't exist - show delete option
            console.log(
              "NotificationDropdown: Analysis does not exist, showing delete option",
            );
            setVerificationFailedNotificationId(notificationId);
            return; // Don't navigate, keep dropdown open
          }

          console.log(
            "NotificationDropdown: Analysis verified, proceeding with navigation",
          );
        }

        // Normalize route names to match expected format
        let normalizedRoute = parsedAction.route;
        if (normalizedRoute === "EmailAnalysis") {
          normalizedRoute = "Email Analysis";
        } else if (normalizedRoute === "AlertDetail") {
          normalizedRoute = "Alerts Intelligence Hub";
        }

        // Navigate to the specified route using setHeroContent
        if (setHeroContent) {
          console.log(
            "NotificationDropdown: Calling setHeroContent with:",
            normalizedRoute,
          );
          setHeroContent(normalizedRoute);

          // If there are URL parameters to set, construct the URL
          if (
            parsedAction.params &&
            Object.keys(parsedAction.params).length > 0
          ) {
            const searchParams = new URLSearchParams(parsedAction.params);
            const newUrl = `${
              window.location.pathname
            }?${searchParams.toString()}`;
            window.history.pushState({}, "", newUrl);

            console.log(
              "NotificationDropdown: Updated URL with parameters:",
              newUrl,
            );

            // Dispatch custom events to notify components of navigation
            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent("notification-navigation", {
                  detail: { params: parsedAction.params },
                }),
              );
              window.dispatchEvent(new CustomEvent("url-changed"));
            }, 100);
          }
        } else {
          console.error(
            "NotificationDropdown: setHeroContent prop not provided",
          );
        }

        // Close dropdown after successful navigation
        setIsOpen(false);
      } else if (parsedAction.type === "external_link" && parsedAction.url) {
        console.log(
          "NotificationDropdown: External link action detected, URL:",
          parsedAction.url,
        );
        window.open(parsedAction.url, "_blank");
        setIsOpen(false);
      } else {
        console.error(
          "NotificationDropdown: Unknown or invalid notification action:",
          parsedAction,
        );
      }
    } catch (error) {
      console.error(
        "NotificationDropdown: Error handling notification action:",
        error,
      );
      // On error, show delete option
      setVerificationFailedNotificationId(notificationId);
    }
  };

  // Ensure notifications is always an array to prevent runtime errors
  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const unreadNotifications = safeNotifications.filter((n) => !n.isRead);
  const hasUnread = unreadNotifications.length > 0;

  return (
    <div
      className={`notification-dropdown ${className} ${disabled ? "disabled" : ""}`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={`notification-toggle ${isOpen ? "active" : ""} ${isActive ? "nav-active" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggleDropdown}
        title={
          disabled
            ? disabledTitle || t("notification.title")
            : t("notification.title")
        }
        aria-disabled={disabled}
        disabled={disabled}
      >
        <FontAwesomeIcon icon={faBell} />
        <NotificationBadge count={unreadCount} />
      </button>

      {!disabled && isOpen && (
        <div className="notification-dropdown-menu">
          <div className="notification-dropdown-header">
            <h3>{t("notification.title")}</h3>
            {hasUnread && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllAsRead}
                title={t("notification.markAllAsRead.tooltip")}
              >
                <FontAwesomeIcon icon={faCheckDouble} />
                {t("notification.markAllRead")}
              </button>
            )}
          </div>

          <div className="notification-dropdown-content">
            {loading && (
              <div className="notification-loading">
                <LoadingSpinner
                  size="sm"
                  message={t("notification.loading")}
                  className="compact"
                />
              </div>
            )}

            {error && (
              <div className="notification-error">
                <p>{error}</p>
                <button onClick={loadNotifications}>{t("common.retry")}</button>
              </div>
            )}

            {!loading && !error && safeNotifications.length === 0 && (
              <div className="notification-empty">
                <FontAwesomeIcon icon={faCheck} />
                <p>{t("notification.empty")}</p>
              </div>
            )}

            {!loading && !error && safeNotifications.length > 0 && (
              <div className="notification-list">
                {safeNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.notificationId}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDismiss={handleDismiss}
                    onAction={handleNotificationAction}
                    verificationFailed={
                      notification.notificationId ===
                      verificationFailedNotificationId
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {safeNotifications.length > 5 && (
            <div className="notification-dropdown-footer">
              <button
                className="view-all-notifications-btn"
                onClick={() => {
                  if (setHeroContent) {
                    setHeroContent("Notifications");
                  }
                  setIsOpen(false);
                }}
              >
                {t("notification.viewAll")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
